import React, { useState, useEffect } from "react";
import { Outlet, useLocation, useNavigate, Navigate } from "react-router-dom";

import {
  Box,
  List,
  ListItem,
  Drawer,
  ListItemButton,
  ListItemText,
  AppBar,
  IconButton,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import ListItemIcon from "@mui/material/ListItemIcon";
import ManageAccountsRoundedIcon from "@mui/icons-material/ManageAccountsRounded";
import ContactEmergencyRoundedIcon from "@mui/icons-material/ContactEmergencyRounded";
import BookmarkAddRoundedIcon from "@mui/icons-material/BookmarkAddRounded";
import ExploreRoundedIcon from "@mui/icons-material/ExploreRounded";
import DriveEtaRoundedIcon from "@mui/icons-material/DriveEtaRounded";
import CardTravelRoundedIcon from "@mui/icons-material/CardTravelRounded";
import MessageTwoToneIcon from "@mui/icons-material/MessageTwoTone";
import SmartToyRoundedIcon from "@mui/icons-material/SmartToyRounded";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import CalendarMonthTwoToneIcon from "@mui/icons-material/CalendarMonthTwoTone";

import { checkAdminRole, checkOper_or_admin } from "./utils/tool";
import { TOKEN_NAME } from "./utils/constDispatch";

function App() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const navigate = useNavigate();

  const [isExpanded, setIsExpanded] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const drawerWidth = isMobile ? 240 : isExpanded ? 200 : 60;

  const token = sessionStorage.getItem(TOKEN_NAME);
  if (!token) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  const styles = {
    box1: { flexGrow: 1, flex: 3, minWidth: 0, overflow: "hidden" },
    appbar: {
      width: isMobile ? "100%" : `calc(100% - ${drawerWidth}px)`,
      ml: isMobile ? 0 : `${drawerWidth}px`,
    },
    drawer: {
      width: isMobile ? 0 : drawerWidth,
      flexShrink: 0,
      "& .MuiDrawer-paper": {
        width: drawerWidth,
        boxSizing: "border-box",
        transition: "width 0.2s",
        backgroundColor: "#F8FAFC",
        borderRight: "1px solid #E8ECF0",
      },
    },
  };

  const getTitle = (pathname) => {
    const map = {
      "/booking": "List Booking",
      "/sample-booking": "Sample Booking",
      "/itinerary": "Itinerary",
      "/location": "Location",
      "/vendor": "Vendor",
      "/operating": "Operating",
      "/calendar-mess": "Calendar Mess",
      "/messtool": "MessTool",
      "/ai-chat": "AI Chat",
      "/user": "User",
    };
    return map[pathname] || "List Booking";
  };

  function ListItemCus({ data_show, icon, to }) {
    const isActive = location.pathname === to;
    const showText = isMobile || isExpanded;
    return (
      <ListItem disablePadding sx={{ px: showText ? 1 : 0.5, mb: 0.3 }}>
        <ListItemButton
          onClick={() => {
            navigate(to);
            if (isMobile) setMobileOpen(false);
          }}
          sx={{
            borderRadius: "8px",
            backgroundColor: isActive ? "#EBF4FF" : "transparent",
            borderLeft: isActive ? "3px solid #1976D2" : "3px solid transparent",
            "&:hover": {
              backgroundColor: isActive ? "#EBF4FF" : "#F0F2F5",
            },
            py: 0.8,
            minHeight: 0,
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: showText ? 36 : "auto",
              color: isActive ? "#1976D2" : "#6B7280",
            }}
          >
            {icon}
          </ListItemIcon>
          {showText && (
            <ListItemText
              primary={data_show}
              primaryTypographyProps={{
                fontSize: 14,
                fontWeight: isActive ? 600 : 400,
                color: isActive ? "#1976D2" : "#374151",
              }}
            />
          )}
        </ListItemButton>
      </ListItem>
    );
  }

  const drawerContent = (
    <List disablePadding>
      {!isMobile && (
        <ListItem
          disablePadding
          sx={{
            borderBottom: "1px solid #E8ECF0",
            mb: 1,
          }}
        >
          <ListItemButton
            onClick={() => setIsExpanded(!isExpanded)}
            sx={{
              py: 1.5,
              justifyContent: isExpanded ? "flex-start" : "center",
            }}
          >
            <ListItemIcon sx={{ minWidth: isExpanded ? 32 : "auto", color: "#6B7280" }}>
              {isExpanded ? <ChevronLeftIcon /> : <MenuIcon />}
            </ListItemIcon>
            {isExpanded && (
              <img
                src="LOGO.jpg"
                alt="Logo"
                style={{ height: 80, objectFit: "contain" }}
              />
            )}
          </ListItemButton>
        </ListItem>
      )}

      {isMobile && (
        <ListItem
          disablePadding
          sx={{
            borderBottom: "1px solid #E8ECF0",
            mb: 1,
            justifyContent: "center",
            py: 1.5,
          }}
        >
          <img
            src="LOGO.jpg"
            alt="Logo"
            style={{ height: 60, objectFit: "contain" }}
          />
        </ListItem>
      )}

      <ListItemCus
        to="/booking"
        data_show="List Booking"
        icon={<BookmarkAddRoundedIcon />}
      />
      {checkOper_or_admin() ? (
        <ListItemCus
          to="/ai-chat"
          data_show="AI Chat"
          icon={<SmartToyRoundedIcon />}
        />
      ) : null}
      <ListItemCus
        to="/sample-booking"
        data_show="Sample Booking"
        icon={<BookmarkAddRoundedIcon />}
      />
      <ListItemCus
        to="/itinerary"
        data_show="Itinerary"
        icon={<DriveEtaRoundedIcon />}
      />
      <ListItemCus
        to="/location"
        data_show="Location"
        icon={<ExploreRoundedIcon />}
      />
      {checkAdminRole() ? (
        <ListItemCus
          to="/vendor"
          data_show="Vendor"
          icon={<ContactEmergencyRoundedIcon />}
        />
      ) : null}
      {checkOper_or_admin() ? (
        <ListItemCus
          to="/operating"
          data_show="Operating"
          icon={<ContactEmergencyRoundedIcon />}
        />
      ) : null}

      {checkOper_or_admin() ? (
        <ListItemCus
          to="/calendar-mess"
          data_show="Calendar Mess"
          icon={<CalendarMonthTwoToneIcon />}
        />
      ) : null}
      {checkAdminRole() ? (
        <ListItemCus
          to="/messtool"
          data_show="MessTool"
          icon={<MessageTwoToneIcon />}
        />
      ) : null}
      <ListItemCus
        to="/user"
        data_show="User"
        icon={<ManageAccountsRoundedIcon />}
      />
    </List>
  );

  return (
    <div style={{ display: "flex" }}>
      {isMobile && (
        <AppBar
          position="fixed"
          sx={{
            backgroundColor: "#1565C0",
            zIndex: theme.zIndex.drawer + 1,
          }}
        >
          <Toolbar sx={{ minHeight: "48px !important" }}>
            <IconButton
              color="inherit"
              edge="start"
              onClick={() => setMobileOpen(!mobileOpen)}
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600 }}>
              {getTitle(location.pathname)}
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={styles.drawer}
        >
          <Box onClick={() => setMobileOpen(false)}>
            {drawerContent}
          </Box>
        </Drawer>
      ) : (
        <Drawer sx={styles.drawer} variant="permanent" anchor="left">
          {drawerContent}
        </Drawer>
      )}

      <Box
        component="main"
        sx={{
          ...styles.box1,
          mt: isMobile ? "48px" : 0,
          width: isMobile ? "100%" : undefined,
        }}
      >
        <Outlet />
      </Box>
    </div>
  );
}

export default App;
